import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Book as SubjectIcon,
  CalendarMonth as WeeklyPlanIcon,
  Assessment as MarksIcon,
  Event as ExamIcon,
  People as MeetingIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ClassManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout, isFeatureEnabled } = useAuth();
  const [loading, setLoading] = useState(true);
  const [classData, setClassData] = useState(null);

  useEffect(() => {
    const fetchClass = async () => {
      try {
        const response = await teacherAPI.getClasses();
        const classes = response.data.classes || [];
        const currentClass = classes.find(c => c.id === id);
        setClassData(currentClass);
      } catch (error) {
        console.error('Error fetching class:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClass();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!classData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" color="error">
          Class not found
        </Typography>
      </Container>
    );
  }

  const managementSections = [
    {
      title: 'Add Subject',
      description: 'Add and manage subjects for this class',
      icon: <SubjectIcon sx={{ fontSize: 60 }} />,
      color: 'primary.main',
      bgColor: 'primary.lighter',
      action: () => navigate(`/teacher/classes/${id}/subjects`),
      featureKey: 'academics'
    },
    {
      title: 'Add Weekly Plan',
      description: 'Create and manage weekly lesson plans',
      icon: <WeeklyPlanIcon sx={{ fontSize: 60 }} />,
      color: 'success.main',
      bgColor: 'success.lighter',
      action: () => navigate(`/teacher/classes/${id}/weekly-plan`),
      featureKey: 'academics'
    },
    {
      title: 'Enter Marks',
      description: 'Enter and manage student marks',
      icon: <MarksIcon sx={{ fontSize: 60 }} />,
      color: 'warning.main',
      bgColor: 'warning.lighter',
      action: () => navigate(`/teacher/classes/${id}/marks`),
      featureKey: 'academics'
    },
    {
      title: 'Add Exam Dates',
      description: 'Schedule exam dates for subjects',
      icon: <ExamIcon sx={{ fontSize: 60 }} />,
      color: 'info.main',
      bgColor: 'info.lighter',
      action: () => navigate(`/teacher/classes/${id}/exam-schedule`),
      featureKey: 'academics'
    },
    {
      title: 'Manage Parent Meetings',
      description: 'Create, schedule, and manage parent-teacher meetings',
      icon: <MeetingIcon sx={{ fontSize: 60 }} />,
      color: 'secondary.main',
      bgColor: 'secondary.lighter',
      action: () => navigate('/teacher/meetings')
    }
  ].filter((section) => !section.featureKey || isFeatureEnabled(section.featureKey));

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            Manage Class: {classData.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {classData.grade && `Grade ${classData.grade}`} {classData.section && `• Section ${classData.section}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher/classes')}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Back to Classes
        </Button>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {managementSections.map((section, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
              onClick={section.action}
            >
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    color: section.color,
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {section.description}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: section.color,
                    '&:hover': {
                      bgcolor: section.color,
                      opacity: 0.9
                    }
                  }}
                >
                  Open
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Information Section */}
      <Paper sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 2, sm: 3 }, bgcolor: 'info.lighter' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
        >
          <strong>Note:</strong> These features will allow you to manage all aspects of your class.
          Click on any card above to get started.
        </Typography>
      </Paper>

      {/* Dialog removed to fix unterminated JSX; re-add if needed */}

    </Container>
  );
};

export default ClassManagement;

